import React, { useState, useEffect, useMemo } from 'react';
import { Save, Check, Edit2, CircleMinus, CircleCheck, Lock } from 'lucide-react';
import { Authority } from './AuthorityTypes';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';

interface AuthorityFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => Promise<void>;
    initialData?: Authority | null;
    authorities: Authority[];
    onToggleActive?: () => Promise<void>;
    canModify?: boolean;
    isApproved?: boolean;
}

const AuthorityForm: React.FC<AuthorityFormProps> = ({ isOpen, onClose, onSubmit, initialData, authorities, onToggleActive, canModify = true, isApproved = false }) => {
    // View-first edit (mirrors the Contracts CNT-15 modal): an existing record
    // opens read-only; the footer shows an Edit button that unlocks the fields
    // in place and swaps the action to Update. Create mode is always editable.
    const [editingBasic, setEditingBasic] = useState(false);
    const fieldsDisabled = !!initialData && (!canModify || isApproved || !editingBasic);
    const fieldVh = useFieldValidation();
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        parent_authority_id: null as number | null,
        is_physical: true
    });
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name,
                description: initialData.description || '',
                parent_authority_id: initialData.parent_authority_id,
                is_physical: initialData.is_physical
            });
        } else {
            setFormData({
                name: '',
                description: '',
                parent_authority_id: null,
                is_physical: true
            });
        }
        fieldVh.reset();
        setSubmitting(false);
        setToggling(false);
        setEditingBasic(false); // existing records open locked; create mode ignores this
    }, [initialData, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // IDs of the current node and all of its descendants — none of them can be a parent.
    const forbiddenParentIds = useMemo(() => {
        const forbidden = new Set<number>();
        if (!initialData) return forbidden;
        forbidden.add(initialData.id);
        const stack = [initialData.id];
        while (stack.length) {
            const cur = stack.pop()!;
            for (const a of authorities) {
                if (a.parent_authority_id === cur && !forbidden.has(a.id)) {
                    forbidden.add(a.id);
                    stack.push(a.id);
                }
            }
        }
        return forbidden;
    }, [initialData, authorities]);

    const runDomainChecks = (): boolean => {
        let ok = true;
        const name = formData.name.trim();
        if (name) {
            const dup = authorities.find(
                (a) => a.id !== initialData?.id && a.is_active !== -1 &&
                    a.name.trim().toLowerCase() === name.toLowerCase(),
            );
            if (dup) { fieldVh.setError('name', 'An authority with this name already exists in this organisation.'); ok = false; }
        }
        if (formData.parent_authority_id != null && initialData && forbiddenParentIds.has(formData.parent_authority_id)) {
            fieldVh.setError('parent_authority_id', formData.parent_authority_id === initialData.id
                ? 'An authority cannot be its own parent.'
                : 'Cannot select a descendant — that would create a hierarchy cycle.');
            ok = false;
        }
        return ok;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const engineOk = fieldVh.validateAll([
            { name: 'name', code: 109, value: formData.name, required: true },
            { name: 'description', code: 110, value: formData.description },
        ]);
        const domainOk = runDomainChecks();
        if (!engineOk || !domainOk) return;
        setSubmitting(true);
        try {
            await onSubmit({ ...formData, name: formData.name.trim() });
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async () => {
        if (!onToggleActive || isApproved) return;
        setToggling(true);
        try {
            await onToggleActive();
        } finally {
            setToggling(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" data-testid="MOD_authority_form">
            <Modal.Header>
                {initialData
                    ? <>
                        {isApproved ? 'Viewing (locked) ' : editingBasic ? 'Editing ' : 'Viewing '}
                        {isApproved && <Lock className="lock-icon lock-icon--md" style={{ marginRight: 6 }} />}
                        "<span className="item-highlight">{initialData.name}</span>"
                      </>
                    : 'Create Authority'}
            </Modal.Header>
            <Modal.Body>
                {isApproved && (
                    <div className="approved-banner" data-testid="BANNER_authority_form_locked">
                        <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                    </div>
                )}
                <form
                    id="authority-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-y-4"
                >
                    {/* System code (AUTH-N) — auto-generated on approve, read-only.
                        Only shown when editing an existing record; null before approval. */}
                    {initialData && (
                        <FormControl.Input
                            label="Code"
                            value={initialData.code || '—'}
                            disabled
                            data-testid="INP_authority_form_code"
                        />
                    )}
                    <ValidatedInput
                        vh={fieldVh}
                        name="name"
                        code={109}
                        required
                        label="Authority Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g. Board of Directors"
                        disabled={fieldsDisabled}
                        data-testid="INP_authority_form_name"
                    />

                    <div className="grid grid-cols-2 gap-4">
                        <div
                            onClick={() => !fieldsDisabled && setFormData({ ...formData, is_physical: true })}
                            data-testid="RAD_authority_form_type_physical"
                            className={`border rounded-lg p-3 flex items-start gap-3 transition-colors ${fieldsDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${formData.is_physical ? 'bg-blue-50 border-blue-300' : 'hover:bg-slate-50 border-slate-200'}`}
                        >
                            <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center ${formData.is_physical ? 'border-blue-600' : 'border-slate-400'}`}>
                                {formData.is_physical && <div className="w-2 h-2 bg-blue-600 rounded-full" />}
                            </div>
                            <div>
                                <span className="block text-sm font-medium text-slate-900">Physical</span>
                                <span className="block text-xs text-slate-500">Physical governing body.</span>
                            </div>
                        </div>

                        <div
                            onClick={() => !fieldsDisabled && setFormData({ ...formData, is_physical: false })}
                            data-testid="RAD_authority_form_type_logical"
                            className={`border rounded-lg p-3 flex items-start gap-3 transition-colors ${fieldsDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${!formData.is_physical ? 'bg-emerald-50 border-emerald-300' : 'hover:bg-slate-50 border-slate-200'}`}
                        >
                            <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center ${!formData.is_physical ? 'border-emerald-600' : 'border-slate-400'}`}>
                                {!formData.is_physical && <div className="w-2 h-2 bg-emerald-600 rounded-full" />}
                            </div>
                            <div>
                                <span className="block text-sm font-medium text-slate-900">Logical</span>
                                <span className="block text-xs text-slate-500">Functional/Logical authority.</span>
                            </div>
                        </div>
                    </div>

                    <FormControl.SearchSelect
                        label="Falls Under"
                        data-testid="SEL_authority_form_parent"
                        value={formData.parent_authority_id}
                        onChange={(value) => {
                            if (fieldsDisabled) return;
                            setFormData({ ...formData, parent_authority_id: value ? Number(value) : null });
                            fieldVh.clearError('parent_authority_id');
                        }}
                        options={authorities
                            // Only approved + active authorities are valid parents; never
                            // offer a cycle-forming ancestor. Keep the currently-selected
                            // parent visible even if it later went inactive/unapproved so
                            // the field still renders its label in edit mode.
                            .filter(a => !forbiddenParentIds.has(a.id))
                            .filter(a =>
                                a.id === formData.parent_authority_id ||
                                (a.is_active === 1 && (a.workflow_state === 'approved' || a.is_approved === 1))
                            )
                            .map(a => ({ value: a.id, label: a.name }))}
                        placeholder="-- None (Top Level) --"
                        clearable
                        error={fieldVh.errors.parent_authority_id}
                        disabled={fieldsDisabled}
                    />

                    <ValidatedTextarea
                        vh={fieldVh}
                        name="description"
                        code={110}
                        label="Description (Optional)"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Brief description of responsibilities..."
                        disabled={fieldsDisabled}
                        data-testid="INP_authority_form_description"
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className={initialData && onToggleActive && !isApproved ? "app-modal-footer--split" : ""}>
                {initialData && onToggleActive && !isApproved && (
                    <Button
                        variant={initialData.is_active ? "inactive-outline" : "active-outline"}
                        icon={initialData.is_active ? CircleMinus : CircleCheck}
                        data-testid="BTN_authority_form_toggle_active"
                        onClick={handleToggleActive}
                        disabled={toggling || submitting}
                    >
                        {toggling ? "..." : initialData.is_active ? "Inactive" : "Active"}
                    </Button>
                )}
                {/* Create mode → Submit */}
                {!initialData && (
                    <Button
                        type="submit"
                        form="authority-form"
                        variant="fill"
                        icon={Save}
                        data-testid="BTN_authority_form_submit"
                        disabled={submitting || toggling}
                    >
                        {submitting ? "Submitting..." : "Submit"}
                    </Button>
                )}
                {/* Edit mode, locked → Edit (unlocks fields in place) */}
                {initialData && canModify && !isApproved && !editingBasic && (
                    <Button
                        variant="fill"
                        icon={Edit2}
                        data-testid="BTN_authority_form_edit"
                        onClick={() => setEditingBasic(true)}
                        disabled={toggling}
                    >
                        Edit
                    </Button>
                )}
                {/* Edit mode, unlocked → Update */}
                {initialData && canModify && !isApproved && editingBasic && (
                    <Button
                        type="submit"
                        form="authority-form"
                        variant="update"
                        icon={Check}
                        data-testid="BTN_authority_form_submit"
                        disabled={submitting || toggling}
                    >
                        {submitting ? "Updating..." : "Update"}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default AuthorityForm;
